//components/GameScreen.jsx
import React, { useState, useMemo, useCallback, useEffect } from "react";
import "./styles/gameScreen.css";
import "./styles/statistiques.css";

import useGameData from "../hooks/useGameData";
import useChatHandler from "../hooks/useChatHandler";
import useNewspaper from "../hooks/useNewspaper";
import usePlayerActions from "../hooks/usePlayerActions";
import useImpacts from "../hooks/useImpacts";
import useMediaQuery from "../hooks/useMediaQuery";
import useTutorial from "../hooks/useTutorial";

import LoadingOverlay from "./LoadingOverlay";
import LeftPanel from "./LeftPanel";
import RightPanel from "./RightPanel";
import Popups from "./Popups";
import HeaderMobile from "./HeaderMobile";
import FooterMobile from "./FooterMobile";
import MobileMap from "./MobileMap";
import Chat from "./Chat";
import MobileData from "./MobileData";
import MobileNews from "./MobileNews";
import MobileStat from "./MobileStat";
import Decret from "./DecretMobile";
import Nucleaire from "./Nucleaire";
import CountryActionPopin from "./CountryActionPopin";
import Header from "./Header";
import TutorialOverlay from "./TutorialOverlay";
import axios from "./AxiosInstance.jsx";

const GameScreen = ({ initialGameState, refreshGameState, stat, isMulti = false, multiState = null, submitMultiTurn = null, gridData = null, turnResolved = false }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { gameState, setGameState, loading, setLoading } = useGameData(initialGameState, refreshGameState, isMulti);
  const {
    pendingInvestments,
    mapActions,
    selectedCountryPopin,
    canPerformAction: _canPerformAction,
    canValidate: _canValidate,
    handleInvest,
    addMapAction,
    removeMapAction,
    handleOpenPopin,
    handleClosePopin,
    resetActions,
    actionMessage,
    setActionMessage
  } = usePlayerActions(gameState);
  const multiGameId = isMulti ? multiState?.game_id : null;
  const { message, setMessage, actions, setActions, chatInputMessage, setChatInputMessage, chatStatus, setChatStatus, handleChat, redirect } = useChatHandler(setLoading, resetActions, multiGameId);
  const { previousArticles, setPreviousArticles, showNewspaperPopup, setShowNewspaperPopup, selectedYear, setSelectedYear, showArchivePopup, setShowArchivePopup, sortedYears, popin } = useNewspaper(setLoading, gameState.year, isMulti);

  const turnSubmitted = isMulti && !!multiState?.your_submitted;
  const canPerformAction = _canPerformAction && !turnSubmitted;
  const canValidate = _canValidate && !turnSubmitted;

  // Multi : récupérer les articles et afficher le journal après résolution du tour
  useEffect(() => {
    if (!isMulti || !turnResolved || !multiGameId) return;
    // Réinitialiser le message d'attente
    setChatStatus("");
    const fetchMultiArticles = async () => {
      try {
        const res = await axios.get(`/api/multi/articles/${multiGameId}`);
        if (res.data && res.data.length > 0) {
          setPreviousArticles(res.data);
          setShowNewspaperPopup(true);
        }
      } catch (err) {
        console.error('Error fetching multi articles:', err);
      }
    };
    fetchMultiArticles();
  }, [isMulti, turnResolved, multiGameId, setPreviousArticles, setShowNewspaperPopup, setChatStatus]);

  const [showNuclear, setShowNuclear] = useState(false);
  const [guerres, setGuerres] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [activeView, setActiveView] = useState('map');
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Intégration du tutoriel
  const {
    tutorialActive,
    currentStep,
    steps,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial
  } = useTutorial(isMobile, setActiveView);

  const impactsByParam = useImpacts(gameState);

  const nuclearValue = gameState.joueur?.nuclear ?? 0;
  const impactNucl = impactsByParam.nuclear?.[0]?.impact ?? 0;
  const hasImpactNucl = impactNucl !== undefined && impactNucl !== null && Number(impactNucl) !== 0;
  const redBtn = nuclearValue > 25;

  const newGame = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/new_game");
      await navigate("/accueil");
      if (response.status != 200) {
        throw new Error("Erreur lors de la création de la nouvelle partie");
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      await refreshGameState();

    } catch (error) {
      console.error("Erreur lors de la requête new_game :", error);
      alert("Impossible de lancer la partie. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const nucleaire = useCallback(() => {
    setShowNuclear(true);
  }, []);

  if (isMobile) {
    return (
      <div className="h-[100dvh] w-screen overflow-hidden bg-slate-900 text-white flex flex-col relative">
        <HeaderMobile
          setShowMenu={setShowMenu}
          showMenu={showMenu}
          setShowConfirm={setShowConfirm}
        />
        <div className="flex-1 relative overflow-auto">


          {activeView === 'map' && <MobileMap gameState={gameState} loading={loading} handleOpenPopin={handleOpenPopin} gridData={gridData} />}
          {activeView === 'chat' && (
            <Chat
              setLoading={setLoading}
              onChat={handleChat}
              setRetour={setMessage}
              backia={message}
              gameState={gameState}
              inputMessage={chatInputMessage}
              setInputMessage={setChatInputMessage}
              chatStatus={chatStatus}
              pendingInvestments={pendingInvestments}
              mapActions={mapActions}
              canValidate={canValidate}
              actionMessage={actionMessage}
              setActionMessage={setActionMessage}
              redirect={redirect}
              turnSubmitted={turnSubmitted}
            />
          )}

          {activeView === 'data' && <MobileData gameState={gameState} impactsByParam={impactsByParam} redBtn={redBtn} nucleaire={nucleaire} nuclearValue={nuclearValue} hasImpactNucl={hasImpactNucl} impactNucl={impactNucl} handleInvest={handleInvest} pendingInvestments={pendingInvestments} canPerformAction={canPerformAction} />}
          {activeView === 'news' && <MobileNews selectedYear={selectedYear} setSelectedYear={setSelectedYear} sortedYears={sortedYears} previousArticles={previousArticles} setShowNewspaperPopup={setShowNewspaperPopup} setShowArchivePopup={setShowArchivePopup} playerCountry={gameState.joueur?.short_name} />}
          {activeView === 'stat' && <MobileStat gameState={gameState} mobile={isMobile} />}
        </div>
        <FooterMobile activeView={activeView} setActiveView={setActiveView} />

        {actions && actions.length > 0 && (
          <div className="absolute inset-0 z-50">
            <Decret
              actions={actions}
              setGameState={setGameState}
              gameState={gameState}
              setActions={setActions}
              setMessage={setMessage}
              message={message}
              setLoading={setLoading}
              setPreviousArticles={setPreviousArticles}
              previousArticles={previousArticles}
              setShowNewspaperPopup={setShowNewspaperPopup}
              onClose={() => setActions([])}
              setGuerres={setGuerres}
              setChatStatus={setChatStatus}
              setActiveView={setActiveView}
              setSelectedYear={setSelectedYear}
              isMulti={isMulti}
              submitMultiTurn={submitMultiTurn}
            />
          </div>
        )}

        {/* Loading Overlay - Affiche les messages de progression */}
        {loading && <LoadingOverlay loading={loading} chatStatus={chatStatus} />}

        {showNuclear && (
          <div className="absolute inset-0 z-50">
            <Nucleaire gameState={gameState} onClose={() => setShowNuclear(false)} />
          </div>
        )}
        {showConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl p-6 shadow-xl max-w-md w-full text-center">
              <h2 className="text-xl font-bold text-red-600 mb-4">Confirmation</h2>
              <p className="text-sm text-gray-700 mb-6">
                Cette action est irréversible et supprimera la partie en cours. Voulez-vous vraiment commencer une nouvelle partie ?
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 rounded bg-gray-500 hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={newGame}
                  disabled={loading}
                  className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                >
                  {loading ? "Chargement..." : "Confirmer"}
                </button>
              </div>
            </div>
          </div>
        )}
        <CountryActionPopin
          selectedCountryPopin={selectedCountryPopin}
          handleClosePopin={handleClosePopin}
          addMapAction={addMapAction}
          removeMapAction={removeMapAction}
          canPerformAction={canPerformAction}
        />
        {tutorialActive && (
          <TutorialOverlay
            step={steps[currentStep]}
            totalSteps={steps.length}
            currentStep={currentStep}
            onNext={nextStep}
            onPrevious={previousStep}
            onSkip={skipTutorial}
            onComplete={completeTutorial}
            isMobile={isMobile}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <Header gameState={gameState} refreshGameState={refreshGameState} />
      <div className="main-container custom-scrollbar" disabled={loading}>
        <LoadingOverlay loading={loading} chatStatus={chatStatus} />

        <LeftPanel
          gameState={gameState}
          impactsByParam={impactsByParam}
          handleInvest={handleInvest}
          pendingInvestments={pendingInvestments}
          canInvest={canPerformAction}
          popin={popin}
          stat={stat}
          nucleaire={nucleaire}
          redBtn={redBtn}
          nuclearValue={nuclearValue}
          hasImpactNucl={hasImpactNucl}
          impactNucl={impactNucl}
        />

        <RightPanel
          gameState={gameState}
          handleOpenPopin={handleOpenPopin}
          setLoading={setLoading}
          handleChat={handleChat}
          setMessage={setMessage}
          message={message}
          chatInputMessage={chatInputMessage}
          setChatInputMessage={setChatInputMessage}
          chatStatus={chatStatus}
          pendingInvestments={pendingInvestments}
          mapActions={mapActions}
          canValidate={canValidate}
          setIsDragging={setIsDragging}
          actionMessage={actionMessage}
          setActionMessage={setActionMessage}
          redirect={redirect}
          gridData={gridData}
          turnSubmitted={turnSubmitted}
        />

        <Popups
          actions={actions}
          setGameState={setGameState}
          gameState={gameState}
          setActions={setActions}
          setMessage={setMessage}
          message={message}
          setLoading={setLoading}
          setPreviousArticles={setPreviousArticles}
          previousArticles={previousArticles}
          setShowNewspaperPopup={setShowNewspaperPopup}
          showArchivePopup={showArchivePopup}
          setShowArchivePopup={setShowArchivePopup}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          sortedYears={sortedYears}
          showNuclear={showNuclear}
          setShowNuclear={setShowNuclear}
          guerres={guerres}
          setGuerres={setGuerres}
          setChatStatus={setChatStatus}
          showNewspaperPopup={showNewspaperPopup}
          isMulti={isMulti}
          submitMultiTurn={submitMultiTurn}
        />
        <CountryActionPopin
          selectedCountryPopin={selectedCountryPopin}
          handleClosePopin={handleClosePopin}
          addMapAction={addMapAction}
          removeMapAction={removeMapAction}
          canPerformAction={canPerformAction}
        />

        {tutorialActive && (
          <TutorialOverlay
            step={steps[currentStep]}
            totalSteps={steps.length}
            currentStep={currentStep}
            onNext={nextStep}
            onPrevious={previousStep}
            onSkip={skipTutorial}
            onComplete={completeTutorial}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(GameScreen);